// Going to zero (spec 10.2). Rekt is not the end: comeback arc or a new life.

import { dgnValue } from "./gas";
import { TUNABLES } from "@degen/shared/config/tunables";
import { dayOf, pushLog, type GameState } from "./state";
import { hasOpenPositions, netWorth } from "./player";

const R = TUNABLES.rekt;

export interface RektInfo {
  tick: number;
  day: number;
  cause: "broke" | "cooked";
  peakNetWorth: number;
  bestTradeUsd: number;
  worstTradeUsd: number;
  daysPlayed: number;
  rugsSurvived: number;
}

function snapshot(state: GameState, cause: RektInfo["cause"]): RektInfo {
  const s = state.player.stats;
  return {
    tick: state.tick,
    day: dayOf(state.tick),
    cause,
    peakNetWorth: s.peakNetWorth,
    bestTradeUsd: s.bestTradeUsd,
    worstTradeUsd: s.worstTradeUsd,
    daysPlayed: s.daysPlayed,
    rugsSurvived: s.rugsSurvived,
  };
}

/**
 * Rekt = net worth under $10 with no open positions (spec).
 * DGN gas is left out: a wallet of gas with nothing to spend it on is still rekt.
 * SPEC-NOTE: also requires a peak of at least $100, otherwise every fresh $0 novice would be rekt on day 1.
 */
export function checkRekt(state: GameState): boolean {
  if (state.rekt) return false;
  const p = state.player;
  if (p.stats.peakNetWorth < R.minPeakNetWorth) return false;
  // Gas money doesn't save you: a few DGN aren't a bag.
  if (netWorth(state) - dgnValue(state) >= R.maxNetWorth) return false;
  if (hasOpenPositions(state) || state.nft.collections.some((c) => c.owned.length > 0)) return false;
  state.rekt = snapshot(state, "broke");
  pushLog(state, "rekt", "You're rekt. It happens to the best of us.");
  return true;
}

/** Settings → "I'm cooked". */
export function imCooked(state: GameState) {
  if (!state.rekt) state.rekt = snapshot(state, "cooked");
}

/** Comeback arc: keep 30% of followers, trust 40, cash $0, +25% engagement for 7 days. */
export function comeback(state: GameState) {
  const p = state.player;
  if (!state.rekt) return false;
  p.followers = Math.round(p.followers * R.comebackFollowersKeep);
  p.trust = R.comebackTrust;
  p.cash = 0;
  p.flags.comebackUntilDay = dayOf(state.tick) + R.comebackDays;
  p.comebacks++;
  // Peak resets so the rekt check needs a fresh run-up before it can fire again.
  p.stats.peakNetWorth = 0;
  state.rekt = null;
  pushLog(state, "comeback", "The comeback arc begins. +25% engagement for 7 days. Make it count.");
  return true;
}
